package com.fantasytenis.shared.domain.lineup.entity

import kotlinx.serialization.Serializable

/**
 * Category types for the 5-slot lineup system
 */
enum class Category(val displayName: String) {
    SERVE("Serve"),
    FOREHAND("Forehand"),
    BACKHAND("Backhand"),
    VOLLEYS("Volleys"),
    FIGHTING("Fighting");

    companion object {
        fun fromString(value: String): Category? {
            return entries.find { it.name.equals(value, ignoreCase = true) }
        }
    }
}

/**
 * Lineup slot assignments
 */
@Serializable
data class LineupSlots(
    val serve: String? = null,
    val forehand: String? = null,
    val backhand: String? = null,
    val volleys: String? = null,
    val fighting: String? = null
) {
    /**
     * Get player ID for a specific category
     */
    fun getPlayerForCategory(category: Category): String? {
        return when (category) {
            Category.SERVE -> serve
            Category.FOREHAND -> forehand
            Category.BACKHAND -> backhand
            Category.VOLLEYS -> volleys
            Category.FIGHTING -> fighting
        }
    }

    /**
     * Create a copy with a specific slot updated
     */
    fun withSlot(category: Category, playerId: String?): LineupSlots {
        return when (category) {
            Category.SERVE -> copy(serve = playerId)
            Category.FOREHAND -> copy(forehand = playerId)
            Category.BACKHAND -> copy(backhand = playerId)
            Category.VOLLEYS -> copy(volleys = playerId)
            Category.FIGHTING -> copy(fighting = playerId)
        }
    }

    /**
     * Get all assigned player IDs
     */
    fun getAllPlayerIds(): List<String> {
        return listOfNotNull(serve, forehand, backhand, volleys, fighting)
    }

    /**
     * Check if all 5 slots are filled
     */
    fun isFilled(): Boolean {
        return serve != null && forehand != null && backhand != null &&
                volleys != null && fighting != null
    }

    /**
     * Count filled slots
     */
    fun filledCount(): Int {
        return listOfNotNull(serve, forehand, backhand, volleys, fighting).size
    }
}

/**
 * Category scores breakdown
 */
@Serializable
data class CategoryScores(
    val serve: Double = 0.0,
    val forehand: Double = 0.0,
    val backhand: Double = 0.0,
    val volleys: Double = 0.0,
    val fighting: Double = 0.0
) {
    val total: Double
        get() = serve + forehand + backhand + volleys + fighting

    fun getScoreForCategory(category: Category): Double {
        return when (category) {
            Category.SERVE -> serve
            Category.FOREHAND -> forehand
            Category.BACKHAND -> backhand
            Category.VOLLEYS -> volleys
            Category.FIGHTING -> fighting
        }
    }
}

/**
 * Slot costs breakdown
 */
@Serializable
data class SlotCosts(
    val serve: Int? = null,
    val forehand: Int? = null,
    val backhand: Int? = null,
    val volleys: Int? = null,
    val fighting: Int? = null
) {
    val total: Int
        get() = listOfNotNull(serve, forehand, backhand, volleys, fighting).sum()

    fun getCostForCategory(category: Category): Int? {
        return when (category) {
            Category.SERVE -> serve
            Category.FOREHAND -> forehand
            Category.BACKHAND -> backhand
            Category.VOLLEYS -> volleys
            Category.FIGHTING -> fighting
        }
    }
}

/**
 * Individual stat contribution to a category score
 */
@Serializable
data class StatLineItem(
    val stat: String,
    val count: Int,
    val multiplier: Int,
    val points: Double
)

/**
 * Breakdown of how a category score was calculated
 */
@Serializable
data class CategoryBreakdown(
    val stats: List<StatLineItem> = emptyList(),
    val quickFinishBonus: Double = 0.0,
    val total: Double = 0.0
)

/**
 * Breakdowns for all 5 category slots
 */
@Serializable
data class CategoryBreakdowns(
    val serve: CategoryBreakdown? = null,
    val forehand: CategoryBreakdown? = null,
    val backhand: CategoryBreakdown? = null,
    val volleys: CategoryBreakdown? = null,
    val fighting: CategoryBreakdown? = null
) {
    fun getBreakdownForCategory(category: Category): CategoryBreakdown? {
        return when (category) {
            Category.SERVE -> serve
            Category.FOREHAND -> forehand
            Category.BACKHAND -> backhand
            Category.VOLLEYS -> volleys
            Category.FIGHTING -> fighting
        }
    }
}

/**
 * Lineup Entity
 *
 * Represents a user's 5-slot fantasy lineup for a contest entry.
 */
@Serializable
data class LineupEntity(
    val id: String,
    val contestEntryId: String,
    val userId: String,
    val contestId: String,
    val slots: LineupSlots,
    val totalSalaryCost: Int,
    val remainingBudget: Int,
    val salaryCap: Int = 35,
    val isValid: Boolean,
    val isLocked: Boolean,
    val validationErrors: List<String>? = null,
    val totalPoints: Double = 0.0,
    val categoryScores: CategoryScores? = null,
    val categoryBreakdowns: CategoryBreakdowns? = null,
    val filledSlots: Int = 0
) {
    /**
     * Check if lineup is under salary cap
     */
    fun isUnderCap(): Boolean = totalSalaryCost <= salaryCap

    /**
     * Check if a specific player is in the lineup
     */
    fun containsPlayer(playerId: String): Boolean {
        return slots.getAllPlayerIds().contains(playerId)
    }

    /**
     * Get the category where a player is assigned (if any)
     */
    fun getCategoryForPlayer(playerId: String): Category? {
        return when (playerId) {
            slots.serve -> Category.SERVE
            slots.forehand -> Category.FOREHAND
            slots.backhand -> Category.BACKHAND
            slots.volleys -> Category.VOLLEYS
            slots.fighting -> Category.FIGHTING
            else -> null
        }
    }
}

/**
 * Validation result from lineup validation
 */
@Serializable
data class ValidationResult(
    val isValid: Boolean,
    val errors: List<String>,
    val warnings: List<String>,
    val totalCost: Int,
    val remainingBudget: Int,
    val slotCosts: SlotCosts
)
